import { useState } from "react";

import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { changedCount, diffFacts, factRows, type DiffRow } from "./facts";

function cellText(row: DiffRow, side: "before" | "after"): string {
  const value = side === "before" ? row.before : row.after;
  return value ?? "";
}

interface BeforeAfterProps {
  before: unknown;
  after: unknown;
  /** The words above the table: "Next version" or "What is proposed". */
  title: string;
}

/**
 * Before and after of a Next version, side by side, with what changed marked. A new element has no "before", so it
 * is shown as a plain list of what is proposed.
 */
export function BeforeAfter({ before, after, title }: BeforeAfterProps) {
  const [showSame, setShowSame] = useState(false);
  const hasBefore = factRows(before).length > 0;

  if (!hasBefore) {
    const rows = factRows(after);
    if (rows.length === 0) return null;
    return (
      <Card className="ra-section" aria-labelledby="ra-after-title">
        <h2 id="ra-after-title">{title}</h2>
        <dl className="ra-facts">
          {rows.map((row) => (
            <div key={row.path} className="ra-facts__row" title={row.reference}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      </Card>
    );
  }

  const rows = diffFacts(before, after);
  const changed = changedCount(rows);
  const shown = showSame ? rows : rows.filter((row) => row.change !== "same");
  return (
    <Card className="ra-section" aria-labelledby="ra-diff-title">
      <h2 id="ra-diff-title">{title}</h2>
      {changed === 0 ? (
        <p className="muted">Nothing differs in the content that is shown here.</p>
      ) : (
        <p className="muted">
          {changed === 1 ? "1 thing differs" : `${changed} things differ`}. Before is what you validated; after is what is proposed.
        </p>
      )}
      {shown.length > 0 && (
        <table className="ra-diff">
          <caption className="sr-only">Before and after</caption>
          <thead>
            <tr>
              <th scope="col">What</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.path} data-change={row.change}>
                <th scope="row">{row.label}</th>
                <td>{cellText(row, "before") || <span className="muted">(nothing)</span>}</td>
                <td>
                  {cellText(row, "after") || <span className="muted">(nothing)</span>}
                  {row.change !== "same" && <span className="sr-only"> ({row.change})</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > changed && (
        <Button variant="quiet" onClick={() => setShowSame((value) => !value)}>
          {showSame ? "Show only what differs" : `Show the ${rows.length - changed} that did not change`}
        </Button>
      )}
    </Card>
  );
}
